/**
 * useJobMatchExplanation — Sprint 2 / TASK 3-6
 *
 * Loads the "Why this job for you?" explanation for a single job.
 *
 * Strategy (in order):
 *   1. Read from `user_job_recommendations` (client-side, instant).
 *      If a row exists and is < 24 h old → use it.
 *   2. If absent or stale → invoke the `explain-job-match` edge function,
 *      which runs inference + LLM-as-Judge and upserts the result.
 *
 * Inputs:
 *   userId — auth.uid(); pass `null` when the session is not yet resolved.
 *   jobId  — peerlist_jobs.id UUID; pass `null` when the drawer is closed.
 *
 * Returns:
 *   status  — 'idle' | 'loading' | 'ready' | 'no_profile' | 'error'
 *   data    — the recommendation row when status === 'ready'
 *   refresh — manually re-trigger (bypasses cache)
 *
 * Hard rules (TASK 3-6):
 *   • DO NOT block drawer render — callers render the panel below the fold
 *     and use the skeleton state while status === 'loading'.
 *   • DO NOT touch useApplyClickLogger or any apply/logging logic.
 *   • Graceful fallback: when no profile vector exists the hook returns
 *     status === 'no_profile' so the UI can show a prompt.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { supabase } from '../../../lib/supabase';

// ── Constants ─────────────────────────────────────────────────────────────────

/** 24 h — matches the edge function cache window. */
const FRESHNESS_MS = 24 * 60 * 60 * 1_000;

// ── Types ─────────────────────────────────────────────────────────────────────

export type MatchBand = 'strong_fit' | 'good_fit' | 'partial_fit' | 'not_recommended';

export interface ScoreBreakdown {
  skill_overlap:        number;
  semantic_similarity:  number;
  experience_alignment: number;
  budget_fit:           number;
  location_fit:         number;
  deadline_feasibility: number;
}

export interface JobMatchExplanation {
  job_id:          string;
  score:           number;
  score_breakdown: ScoreBreakdown;
  matched_skills:  string[];
  missing_skills:  string[];
  bonus_skills:    string[];
  why_matched:     string;
  skill_gaps:      string[];
  action_items:    string[];
  band:            MatchBand;
  llm_confidence?: number | null;
  generated_at:    string;
}

export type ExplanationStatus = 'idle' | 'loading' | 'ready' | 'no_profile' | 'error';

export interface UseJobMatchExplanationResult {
  status:  ExplanationStatus;
  data:    JobMatchExplanation | null;
  refresh: () => void;
}

// ── DB row shape ──────────────────────────────────────────────────────────────

interface DbRecommendationRow {
  job_id:          string;
  score:           number;
  score_breakdown: ScoreBreakdown | null;
  why_matched:     string | null;
  skill_gaps:      string[] | null;
  action_items:    string[] | null;
  /** Not a DB column — populated only when returned by the edge function. */
  band?:           MatchBand | null;
  /** Not a DB column — populated only when returned by the edge function. */
  llm_confidence?: number | null;
  generated_at:    string;
}

function rowToExplanation(row: DbRecommendationRow): JobMatchExplanation {
  const score = Number(row.score);
  return {
    job_id:          row.job_id,
    score,
    score_breakdown: row.score_breakdown ?? {
      skill_overlap:        0,
      semantic_similarity:  0,
      experience_alignment: 50,
      budget_fit:           50,
      location_fit:         50,
      deadline_feasibility: 50,
    },
    matched_skills:  [],
    missing_skills:  row.skill_gaps ?? [],
    bonus_skills:    [],
    why_matched:     row.why_matched ?? '',
    skill_gaps:      row.skill_gaps ?? [],
    action_items:    row.action_items ?? [],
    band:            row.band ?? bandFromScore(score),
    llm_confidence:  row.llm_confidence ?? null,
    generated_at:    row.generated_at,
  };
}

function isFresh(generatedAt: string): boolean {
  return Date.now() - new Date(generatedAt).getTime() < FRESHNESS_MS;
}

function bandFromScore(score: number): MatchBand {
  if (score >= 75) return 'strong_fit';
  if (score >= 55) return 'good_fit';
  if (score >= 35) return 'partial_fit';
  return 'not_recommended';
}

// ── Edge function shape ───────────────────────────────────────────────────────

interface EdgeResponse {
  success:          boolean;
  no_profile_vector?: boolean;
  error?:           string;
  data?:            JobMatchExplanation;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useJobMatchExplanation(
  userId: string | null,
  jobId:  string | null,
): UseJobMatchExplanationResult {
  const [status, setStatus] = useState<ExplanationStatus>('idle');
  const [data,   setData]   = useState<JobMatchExplanation | null>(null);

  // Stable ref tracks the current (userId, jobId) pair to cancel stale loads.
  const keyRef     = useRef<string>('');
  const refreshKey = useRef(0);

  const load = useCallback(async (
    uid: string,
    jid: string,
    forceRefresh: boolean,
    signal: AbortSignal,
  ): Promise<void> => {
    setStatus('loading');
    setData(null);

    // ── Step 1: client-side read from user_job_recommendations ────────────────
    // NOTE: `band` and `llm_confidence` are NOT columns in the DB table — they
    // are derived/optional. `band` is computed from `score`; `llm_confidence`
    // is written by the edge function only when a `band` column migration has
    // been applied. Omit them from the SELECT to avoid a 42703 Postgres error.
    if (!forceRefresh) {
      const { data: rows, error: dbErr } = await supabase
        .from('user_job_recommendations')
        .select(
          'job_id, score, score_breakdown, why_matched, skill_gaps, action_items, generated_at',
        )
        .eq('user_id', uid)
        .eq('job_id', jid)
        .maybeSingle<DbRecommendationRow>();

      if (signal.aborted) return;

      if (!dbErr && rows && isFresh(rows.generated_at)) {
        setData(rowToExplanation(rows));
        setStatus('ready');
        return;
      }
    }

    // ── Step 2: invoke explain-job-match edge function ────────────────────────
    const { data: { session } } = await supabase.auth.getSession();
    if (signal.aborted) return;

    if (!session?.access_token) {
      setStatus('error');
      return;
    }

    let edgeRes: EdgeResponse;
    try {
      const { data: fnData, error: fnErr } = await supabase.functions.invoke<EdgeResponse>(
        'explain-job-match',
        { body: { job_id: jid } },
      );
      if (signal.aborted) return;
      if (fnErr) throw fnErr;
      edgeRes = fnData as EdgeResponse;
    } catch {
      if (signal.aborted) return;
      setStatus('error');
      return;
    }

    if (signal.aborted) return;

    if (edgeRes.no_profile_vector) {
      // Profile not yet vectorized — trigger the upsert silently, then retry once.
      console.log('[useJobMatchExplanation] no_profile_vector — triggering upsert-profile-vector');
      try {
        await supabase.functions.invoke('upsert-profile-vector');
        if (signal.aborted) return;
        // Give Pinecone 2 s to index the new vector before retrying.
        await new Promise<void>((resolve) => setTimeout(resolve, 2_000));
        if (signal.aborted) return;

        const { data: retryData, error: retryErr } = await supabase.functions.invoke<EdgeResponse>(
          'explain-job-match',
          { body: { job_id: jid } },
        );
        if (signal.aborted) return;
        if (!retryErr && retryData?.success && retryData?.data) {
          setData(retryData.data);
          setStatus('ready');
          return;
        }
      } catch {
        if (signal.aborted) return;
      }
      // If retry also failed, fall back to no_profile so the UI can prompt.
      setStatus('no_profile');
      return;
    }

    if (!edgeRes.success || !edgeRes.data) {
      setStatus('error');
      return;
    }

    setData(edgeRes.data);
    setStatus('ready');
  }, []);

  // Track refresh requests (increment → triggers effect below).
  const refreshCounter = useRef(0);
  const [refreshTick, setRefreshTick] = useState(0);

  const refresh = useCallback(() => {
    refreshCounter.current++;
    setRefreshTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!userId || !jobId) {
      setStatus('idle');
      setData(null);
      return;
    }

    const key = `${userId}::${jobId}`;
    keyRef.current = key;
    const forceRefresh = refreshTick > 0 && refreshKey.current !== refreshTick;
    refreshKey.current = refreshTick;

    const controller = new AbortController();
    void load(userId, jobId, forceRefresh, controller.signal);

    return () => {
      controller.abort();
      // Reset to idle if the pair has changed (prevents stale panel flash).
      if (keyRef.current !== key) {
        setStatus('idle');
        setData(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, jobId, refreshTick, load]);

  return { status, data, refresh };
}
